import { Link, usePage } from "@inertiajs/react";
import clsx from "clsx";
import type { PublicProps } from "@/types";
import { navItemsFor, type NavItem } from "./NavItems";
import UserCard from "./UserCard";
import Icon from "@/components/ui/Icon";
import logoWordmark from "@/assets/brand/logo-wordmark.png";

function SideNavLink({ item }: { item: NavItem }) {
  const { url } = usePage<PublicProps>();
  const active = url.startsWith(item.link);

  return (
    <Link
      href={item.link}
      className={clsx(
        "group font-display text-subheading duration-fast ease-standard relative flex items-center gap-3 border-2 px-3 py-2.5 font-bold transition-all",
        active
          ? "border-line bg-goldenrod text-ink shadow-control"
          : "text-ink hover:border-line hover:bg-surface border-transparent",
      )}
    >
      <span className="relative flex h-8 w-8 shrink-0 items-center justify-center">
        <Icon src={item.icon} className="h-6 w-6" />
        {(item.badge ?? 0) > 0 && (
          <span className="border-line bg-sunset-orange absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full border px-1 text-[10px] font-bold text-white">
            {item.badge}
          </span>
        )}
      </span>
      {item.name}
    </Link>
  );
}

export default function SideNav() {
  const { props } = usePage<PublicProps>();
  const items = navItemsFor(props.user, props.program);

  return (
    // Pinned to the viewport so only the main column scrolls. h-screen is what
    // makes sticky work here - as a flex item the aside would otherwise stretch
    // to the full page height and have nothing to stick to.
    <aside className="border-line/20 bg-surface-alt hidden w-72 shrink-0 flex-col gap-6 border-r-2 px-4 py-6 lg:sticky lg:top-0 lg:flex lg:h-screen lg:overflow-y-auto">
      <Link href="/home" className="px-1">
        <img
          src={logoWordmark}
          alt={props.program.name}
          className="h-20 w-full object-contain object-left"
        />
      </Link>

      <nav className="flex flex-col gap-1.5">
        {items.map((item) => (
          <SideNavLink key={item.link} item={item} />
        ))}
      </nav>

      <div className="mt-auto flex flex-col gap-4">
        <SupportBox />
        {props.user ? <UserCard /> : <SignInCard />}
      </div>
    </aside>
  );
}

// Logged-out visitors land here from a shared event link.
function SignInCard() {
  return (
    <a
      href="/auth/hca"
      className="border-line bg-goldenrod text-ink shadow-card font-display block border-2 p-3 text-center font-bold"
    >
      Log in
    </a>
  );
}

export function SupportBox() {
  const { props } = usePage<PublicProps>();
  const { support_channel_name, support_channel_url, support_email } =
    props.program;

  if (!support_channel_name && !support_email) return null;

  return (
    <div className="border-line/30 bg-sidecar/50 text-caption text-ink-muted border-2 p-3 text-center">
      {support_channel_name && (
        <>
          Ask in{" "}
          <a
            className="font-bold underline"
            target="_blank"
            rel="noreferrer"
            href={support_channel_url ?? undefined}
          >
            #{support_channel_name}
          </a>
        </>
      )}
      {support_channel_name && support_email && " or "}
      {support_email && (
        <>
          email{" "}
          <a className="font-bold underline" href={`mailto:${support_email}`}>
            {support_email}
          </a>
        </>
      )}{" "}
      if you need help!
    </div>
  );
}
